import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import axios from 'axios'
import moment from 'moment'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Grid, IconButton, InputAdornment, MenuItem, TextField,
  Tooltip, Typography
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import MuiAlert from '@material-ui/lab/Alert'
import { KeyboardDatePicker, KeyboardTimePicker } from '@material-ui/pickers'
import AddIcon from '@material-ui/icons/Add'
import RemoveIcon from '@material-ui/icons/Remove'
import ReplayIcon from '@material-ui/icons/Replay'
import OpenInNewIcon from '@material-ui/icons/OpenInNew'

const ASPIRE_BASE_URL = process.env.REACT_APP_ASPIRE_BASE_URL || 'https://cloud.youraspire.com'

const WIND_DIRECTIONS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']

const STEP_LABELS = {
  ticketLink: 'Ticket Link',
  pdfAttach: 'PDF Attach',
  timePush: 'Time Push',
  markReviewed: 'Mark Reviewed',
  ticketComplete: 'Ticket Complete'
}

const stepChipColor = (status) => {
  if (status === 'done') return { backgroundColor: '#e8f5e9', color: '#2e7d32' }
  if (status === 'failed') return { backgroundColor: '#ffebee', color: '#c62828' }
  return { backgroundColor: '#eeeeee', color: '#616161' }
}

const newMutationId = () => `msl-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

const emptyChemicalRow = () => ({ chemical: null, name: '', amountOz: '' })

// Combine a service date with a time-picker value into one Date
const combineDateTime = (date, time) => {
  if (!date || !time) return null
  const d = moment(date)
  const t = moment(time)
  return d.clone().hour(t.hour()).minute(t.minute()).second(0).millisecond(0).toDate()
}

// variant 'modal' (default): centered dialog. variant 'side': non-modal
// right-docked sheet — no backdrop, page behind stays visible and usable
// (used by bulk uploads so the scan can be read while keying data).
const ManualSprayLogDialog = ({ open, onClose, url, item = null, handwrittenUrl = null, variant = 'modal' }) => {
  const mutationIdRef = useRef(newMutationId())

  // Context data
  const [context, setContext] = useState({ sprayTechs: [], chemicals: [], blends: [], routes: [] })
  const [contextLoading, setContextLoading] = useState(false)

  // Property selection (when not locked to an item)
  const [property, setProperty] = useState(item)
  const [propertyOptions, setPropertyOptions] = useState([])
  const [propertySearch, setPropertySearch] = useState('')
  const [propertyLoading, setPropertyLoading] = useState(false)

  // Work ticket candidates
  const [ticketOptions, setTicketOptions] = useState([])
  const [ticketNote, setTicketNote] = useState('')
  const [ticketAlert, setTicketAlert] = useState(null)
  const [ticketsLoading, setTicketsLoading] = useState(false)

  // Form state
  const [form, setForm] = useState({
    date: new Date(),
    route: null,
    employee: null,
    ticket: null,
    startTime: null,
    stopTime: null,
    temp: '',
    windSpeed: '',
    windDir: '',
    waterStart: '',
    waterEnd: '',
    blend: null,
    chemicalRows: [emptyChemicalRow()],
    notes: ''
  })

  const [handwrittenFile, setHandwrittenFile] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [duplicateWarning, setDuplicateWarning] = useState(false)
  const [result, setResult] = useState(null)
  const [retrying, setRetrying] = useState(false)

  const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }))

  // Reset per open
  useEffect(() => {
    if (!open) return
    mutationIdRef.current = newMutationId()
    setProperty(item || null)
    setResult(null)
    setSubmitError('')
    setHandwrittenFile(null)
    setForm({
      date: new Date(),
      route: null,
      employee: null,
      ticket: null,
      startTime: null,
      stopTime: null,
      temp: '',
      windSpeed: '',
      windDir: '',
      waterStart: '',
      waterEnd: '',
      blend: null,
      chemicalRows: [emptyChemicalRow()],
      notes: ''
    })
  }, [open, item])

  // Load dropdown context once per open
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setContextLoading(true)
    axios.get(`${url}/api/herbicide-schedule/manual-spray-log-context`)
      .then(resp => { if (!cancelled) setContext(resp.data || {}) })
      .catch(err => console.error('Error loading spray log context:', err))
      .finally(() => { if (!cancelled) setContextLoading(false) })
    return () => { cancelled = true }
  }, [open, url])

  // Property search (debounced)
  useEffect(() => {
    if (!open || item || propertySearch.length < 2) return
    const timer = setTimeout(async () => {
      setPropertyLoading(true)
      try {
        const resp = await axios.get(`${url}/api/herbicide-schedule`, {
          params: { search: propertySearch, limit: 20, page: 0 }
        })
        setPropertyOptions(resp.data?.items || [])
      } catch (err) {
        console.error('Error searching properties:', err)
      }
      setPropertyLoading(false)
    }, 300)
    return () => clearTimeout(timer)
  }, [open, item, propertySearch, url])

  // Ticket candidates when a property is chosen
  const loadTickets = useCallback(async () => {
    if (!property?._id) { setTicketOptions([]); setTicketAlert(null); return }
    setTicketNote('')
    setTicketAlert(null)
    setTicketsLoading(true)
    try {
      const resp = await axios.get(`${url}/api/herbicide-schedule/${property._id}/work-ticket-candidates`, {
        params: { includeCompleted: true }
      })
      const tickets = resp.data?.tickets || []
      setTicketOptions(tickets)
      // Prefer an open ticket; fall back to the most recently completed one
      // (the back-fill case: work done + completed in Aspire, log never attached)
      const preferred = tickets.find(t => t.statusId !== 7 && t.statusId !== 10) || tickets[0] || null
      setForm(prev => ({ ...prev, ticket: preferred }))
      if (!tickets.length) {
        setTicketAlert({
          severity: 'warning',
          message: 'No Aspire work tickets found for this property. You can save the log without one (it will be flagged for reconciliation), or create the ticket in Aspire first and refresh.',
          aspirePropertyId: resp.data?.propertyId || property.aspirePropertyId || null
        })
      }
    } catch (err) {
      setTicketOptions([])
      setForm(prev => ({ ...prev, ticket: null }))
      if (err.response?.data?.error === 'aspire_property_required') {
        setTicketAlert({
          severity: 'info',
          message: 'No Aspire property is linked to this site, so no work ticket can be attached. The log will be saved and flagged for office reconciliation (needs work ticket).',
          aspirePropertyId: null
        })
      } else {
        setTicketNote('Could not load work tickets — you can still save without one.')
      }
    }
    setTicketsLoading(false)
  }, [property, url])

  useEffect(() => {
    if (!open) return
    loadTickets()
  }, [open, loadTickets])

  const applyBlend = (blend) => {
    setForm(prev => {
      if (!blend) return { ...prev, blend: null }
      const rows = (blend.chemicals || []).map(bc => {
        const match = (context.chemicals || []).find(c =>
          String(c._id) === String(bc.chemicalId) || c.name === bc.name
        )
        return { chemical: match || null, name: bc.name, amountOz: '' }
      })
      return { ...prev, blend, chemicalRows: rows.length ? rows : [emptyChemicalRow()] }
    })
  }

  const updateChemicalRow = (index, patch) => {
    setForm(prev => ({
      ...prev,
      chemicalRows: prev.chemicalRows.map((row, i) => (i === index ? { ...row, ...patch } : row))
    }))
  }

  // Aspire locks time entry after the 5th of the month following the service
  // date — mirror of the server-side guard in manualSprayLog.service.js.
  const timePushLocked = useMemo(() => {
    if (!form.date) return false
    const lockDeadline = moment(form.date).add(1, 'month').startOf('month').date(5).endOf('day')
    return moment().isAfter(lockDeadline)
  }, [form.date])

  const waterUsed = useMemo(() => {
    const start = parseFloat(form.waterStart)
    const end = parseFloat(form.waterEnd)
    if (!Number.isFinite(start) || !Number.isFinite(end)) return ''
    return Math.max(0, start - end)
  }, [form.waterStart, form.waterEnd])

  const validation = useMemo(() => {
    const errors = []
    if (!property) errors.push('property')
    if (!form.date) errors.push('date')
    if (!form.route) errors.push('route')
    if (!form.employee) errors.push('employee')
    if (!form.startTime) errors.push('start time')
    if (!form.stopTime) errors.push('stop time')
    if (form.startTime && form.stopTime) {
      const start = combineDateTime(form.date, form.startTime)
      const stop = combineDateTime(form.date, form.stopTime)
      if (start && stop && stop <= start) errors.push('stop after start')
    }
    if (form.temp === '') errors.push('temp')
    if (form.windSpeed === '') errors.push('wind speed')
    if (!form.windDir) errors.push('wind direction')
    if (form.waterStart === '') errors.push('water start')
    if (form.waterEnd === '') errors.push('water end')
    if (form.waterStart !== '' && form.waterEnd !== '' &&
        parseFloat(form.waterEnd) > parseFloat(form.waterStart)) {
      errors.push('water end ≤ water start')
    }
    const validRows = form.chemicalRows.filter(row =>
      (row.chemical || row.name) && parseFloat(row.amountOz) > 0
    )
    if (!validRows.length) errors.push('at least one chemical with ounces')
    return { errors, valid: errors.length === 0, validRows }
  }, [property, form])

  const handleSubmit = async (force = false) => {
    if (!validation.valid || !property) return
    setSubmitting(true)
    setSubmitError('')
    setDuplicateWarning(false)
    try {
      // Handwritten original: from the bulk-upload viewer (already hosted) or
      // uploaded now via presigned URL
      let resolvedHandwrittenUrl = handwrittenUrl || null
      if (!resolvedHandwrittenUrl && handwrittenFile) {
        const presign = await axios.post(`${url}/api/hmi/unclaimed-work/presigned-url`, {
          fileName: `spray-log-scan-${handwrittenFile.name}`,
          contentType: handwrittenFile.type || 'application/octet-stream'
        })
        const { uploadUrl, publicUrl } = presign.data?.data || {}
        if (!uploadUrl) throw new Error('Could not get upload URL for handwritten log')
        await axios.put(uploadUrl, handwrittenFile, {
          headers: { 'Content-Type': handwrittenFile.type || 'application/octet-stream', 'x-amz-acl': 'public-read' },
          withCredentials: false
        })
        resolvedHandwrittenUrl = publicUrl
      }

      const payload = {
        force,
        handwrittenUrl: resolvedHandwrittenUrl,
        clientMutationId: mutationIdRef.current,
        date: moment(form.date).startOf('day').toISOString(),
        aspireRouteId: form.route?.RouteID,
        employeeId: form.employee?._id,
        workTicketId: form.ticket?.workTicketId || null,
        startTime: combineDateTime(form.date, form.startTime)?.toISOString(),
        stopTime: combineDateTime(form.date, form.stopTime)?.toISOString(),
        temp: parseFloat(form.temp),
        windSpeed: parseFloat(form.windSpeed),
        windDir: form.windDir,
        waterStart: parseFloat(form.waterStart),
        waterEnd: parseFloat(form.waterEnd),
        chemicals: {
          blendId: form.blend?._id || null,
          items: validation.validRows.map(row => ({
            chemicalId: row.chemical?._id || null,
            name: row.chemical?.name || row.name,
            epaNum: row.chemical?.herbicideDetails?.epaRegistrationNumber || null,
            amountOz: parseFloat(row.amountOz)
          }))
        },
        notes: form.notes
      }
      const resp = await axios.post(
        `${url}/api/herbicide-schedule/${property._id}/manual-spray-log`,
        payload
      )
      setResult(resp.data)
    } catch (err) {
      console.error('Error saving manual spray log:', err)
      if (err.response?.data?.code === 'already_logged_for_date') {
        setDuplicateWarning(true)
      } else {
        setSubmitError(err.response?.data?.error || err.response?.data?.code || 'Failed to save spray log')
      }
    }
    setSubmitting(false)
  }

  const handleRetry = async () => {
    if (!property) return
    setRetrying(true)
    try {
      const resp = await axios.post(`${url}/api/herbicide-schedule/${property._id}/retry-closeout`)
      if (resp.data?.closeout) {
        setResult(prev => ({ ...prev, closeout: resp.data.closeout }))
      }
    } catch (err) {
      console.error('Error retrying closeout:', err)
    }
    setRetrying(false)
  }

  const handleClose = useCallback(() => {
    onClose(!!result)
  }, [onClose, result])

  const steps = result?.closeout?.steps || {}
  const anyStepFailed = Object.values(steps).some(step => step?.status === 'failed')

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={variant === 'side' ? false : 'md'}
      fullWidth={variant !== 'side'}
      hideBackdrop={variant === 'side'}
      disableEnforceFocus={variant === 'side'}
      disableScrollLock={variant === 'side'}
      style={variant === 'side' ? { pointerEvents: 'none' } : undefined}
      PaperProps={variant === 'side' ? {
        elevation: 8,
        style: {
          pointerEvents: 'auto',
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          margin: 0,
          height: '100%',
          maxHeight: '100%',
          width: 620,
          maxWidth: '85vw',
          borderRadius: 0,
          borderLeft: '2px solid #ddd'
        }
      } : undefined}
    >
      <DialogTitle>
        Log Spray — Manual Entry
        {property && (
          <Typography variant="body2" color="textSecondary">
            {(property.aspirePropertyId || property.canonicalPropertyId) ? (
              <a
                href={`${ASPIRE_BASE_URL}/app/properties/overview/${property.aspirePropertyId || property.canonicalPropertyId}`}
                target="_blank" rel="noopener noreferrer"
                style={{ color: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                title="Open property in Aspire"
              >
                {property.siteName} {property.address ? `— ${property.address}` : ''}
                <OpenInNewIcon style={{ fontSize: 14 }} />
              </a>
            ) : (
              <>{property.siteName} {property.address ? `— ${property.address}` : ''}</>
            )}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {result ? (
          <Box py={1}>
            <Typography variant="subtitle1" gutterBottom>
              Spray log saved{result.replayed ? ' (already recorded — replayed)' : ''}.
            </Typography>
            {result.pdfUrl && (
              <Typography variant="body2" gutterBottom>
                PDF: <a href={result.pdfUrl} target="_blank" rel="noopener noreferrer">{result.pdfUrl}</a>
              </Typography>
            )}
            <Box display="flex" style={{ gap: 8, flexWrap: 'wrap' }} my={1.5}>
              {Object.entries(STEP_LABELS).map(([key, label]) => {
                const step = steps[key] || {}
                const chip = (
                  <Chip
                    key={key}
                    size="small"
                    label={`${label}: ${step.status || 'n/a'}`}
                    style={stepChipColor(step.status)}
                  />
                )
                return step.error
                  ? <Tooltip key={key} title={step.error}>{chip}</Tooltip>
                  : chip
              })}
            </Box>
            {result.closeout?.status === 'needs_review' && (
              <Typography variant="body2" color="error">
                Some Aspire steps failed — you can retry them now or later from the queue.
              </Typography>
            )}
          </Box>
        ) : (
          <Grid container spacing={2}>
            {/* ── Who / where / when ── */}
            <Grid item xs={12}>
              <Autocomplete
                options={item ? [item] : propertyOptions}
                getOptionLabel={(option) => `${option.siteName || ''}${option.address ? ` — ${option.address}` : ''}`}
                value={property}
                disabled={!!item}
                loading={propertyLoading}
                onChange={(e, value) => setProperty(value)}
                onInputChange={(e, value) => setPropertySearch(value)}
                renderInput={(params) => (
                  <TextField {...params} label="Property" variant="outlined" size="small" required
                    helperText={!item ? 'Search the herbicide queue by site name or address' : null} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <KeyboardDatePicker
                label="Service Date" format="MM/dd/yyyy" value={form.date}
                onChange={(date) => setField('date', date)}
                variant="inline" inputVariant="outlined" size="small" fullWidth required autoOk
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={context.routes || []}
                getOptionLabel={(option) => option.RouteName || `Route ${option.RouteID}`}
                value={form.route}
                onChange={(e, value) => setField('route', value)}
                loading={contextLoading}
                renderInput={(params) => (
                  <TextField {...params} label="Route" variant="outlined" size="small" required />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={context.sprayTechs || []}
                getOptionLabel={(option) => `${option.firstName || ''} ${option.lastName || ''}`.trim()}
                value={form.employee}
                onChange={(e, value) => setField('employee', value)}
                loading={contextLoading}
                renderInput={(params) => (
                  <TextField {...params} label="Spray Tech" variant="outlined" size="small" required />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
              <Autocomplete
                style={{ flex: 1 }}
                options={ticketOptions}
                getOptionLabel={(option) => {
                  const dateStr = option.statusId === 7 && option.completeDate
                    ? `completed ${moment(option.completeDate).format('MM/DD/YYYY')}`
                    : (option.scheduledDate ? moment(option.scheduledDate).format('MM/DD/YYYY') : '')
                  return `#${option.workTicketNumber || option.workTicketId} — ${option.serviceName || 'Service'} (${option.status || 'status n/a'}${dateStr ? `, ${dateStr}` : ''})`
                }}
                value={form.ticket}
                disabled={!property}
                onChange={(e, value) => setField('ticket', value)}
                renderInput={(params) => (
                  <TextField {...params} label="Aspire Work Ticket (optional)" variant="outlined" size="small"
                    helperText={
                      (form.ticket?.statusId === 7 &&
                        'Ticket is already complete in Aspire — the spray log PDF will be attached; time push and ticket completion will be skipped.') ||
                      (form.ticket && timePushLocked &&
                        'Aspire time entry is locked for this service date (past the 5th of the following month) — hours will not be pushed; the spray log will still be attached.') ||
                      ticketNote || (!property ? 'Choose a property first' : null)
                    } />
                )}
              />
              <Tooltip title={form.ticket ? `Open work ticket #${form.ticket.workTicketNumber || form.ticket.workTicketId} in Aspire` : 'Select a ticket to view it in Aspire'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={!form.ticket}
                    href={form.ticket ? `${ASPIRE_BASE_URL}/app/worktickets/details/${form.ticket.workTicketId}` : undefined}
                    target="_blank" rel="noopener noreferrer"
                    style={{ marginTop: 6 }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              </Box>
              {ticketAlert && (
                <MuiAlert severity={ticketAlert.severity} style={{ marginTop: 8 }}>
                  {ticketAlert.message}
                  <Box display="flex" style={{ gap: 8 }} mt={1}>
                    {ticketAlert.aspirePropertyId && (
                      <Button
                        size="small" variant="outlined" color="primary"
                        startIcon={<OpenInNewIcon />}
                        href={`${ASPIRE_BASE_URL}/app/properties/overview/${ticketAlert.aspirePropertyId}`}
                        target="_blank" rel="noopener noreferrer"
                      >
                        Open Property in Aspire
                      </Button>
                    )}
                    <Button
                      size="small" variant="outlined"
                      startIcon={ticketsLoading ? <CircularProgress size={14} /> : <ReplayIcon />}
                      onClick={loadTickets} disabled={ticketsLoading}
                    >
                      Refresh Tickets
                    </Button>
                  </Box>
                </MuiAlert>
              )}
            </Grid>

            {/* ── Conditions & times ── */}
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={6} sm={3}>
              <KeyboardTimePicker
                label="Start Time" value={form.startTime}
                onChange={(time) => setField('startTime', time)}
                variant="inline" inputVariant="outlined" size="small" fullWidth required autoOk
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <KeyboardTimePicker
                label="Stop Time" value={form.stopTime}
                onChange={(time) => setField('stopTime', time)}
                variant="inline" inputVariant="outlined" size="small" fullWidth required autoOk
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                label="Temp" value={form.temp} onChange={(e) => setField('temp', e.target.value)}
                variant="outlined" size="small" fullWidth required type="number"
                InputProps={{ endAdornment: <InputAdornment position="end">°F</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                label="Wind" value={form.windSpeed} onChange={(e) => setField('windSpeed', e.target.value)}
                variant="outlined" size="small" fullWidth required type="number"
                InputProps={{ endAdornment: <InputAdornment position="end">mph</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField
                select label="Dir" value={form.windDir} onChange={(e) => setField('windDir', e.target.value)}
                variant="outlined" size="small" fullWidth required
              >
                {WIND_DIRECTIONS.map((dir) => <MenuItem key={dir} value={dir}>{dir}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Water Start" value={form.waterStart} onChange={(e) => setField('waterStart', e.target.value)}
                variant="outlined" size="small" fullWidth required type="number"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Water End" value={form.waterEnd} onChange={(e) => setField('waterEnd', e.target.value)}
                variant="outlined" size="small" fullWidth required type="number"
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label="Water Used" value={waterUsed} variant="outlined" size="small" fullWidth disabled
                InputProps={{ endAdornment: <InputAdornment position="end">gal</InputAdornment> }}
              />
            </Grid>

            {/* ── Chemicals ── */}
            <Grid item xs={12}><Divider /></Grid>
            <Grid item xs={12} sm={5}>
              <Autocomplete
                options={context.blends || []}
                getOptionLabel={(option) => option.name || ''}
                value={form.blend}
                onChange={(e, value) => applyBlend(value)}
                loading={contextLoading}
                renderInput={(params) => (
                  <TextField {...params} label="Blend (optional — auto-fills rows)" variant="outlined" size="small" />
                )}
              />
            </Grid>
            {form.chemicalRows.map((row, index) => (
              <Grid item xs={12} key={index}>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <Autocomplete
                    style={{ flex: 2 }}
                    options={context.chemicals || []}
                    getOptionLabel={(option) =>
                      `${option.name}${option.herbicideDetails?.epaRegistrationNumber ? ` (EPA: ${option.herbicideDetails.epaRegistrationNumber})` : ''}`}
                    value={row.chemical}
                    onChange={(e, value) => updateChemicalRow(index, { chemical: value, name: value?.name || row.name })}
                    renderInput={(params) => (
                      <TextField {...params} label="Chemical" variant="outlined" size="small"
                        helperText={!row.chemical && row.name ? `From blend: ${row.name}` : null} />
                    )}
                  />
                  <TextField
                    style={{ flex: 1 }}
                    label="Amount (oz)" value={row.amountOz}
                    onChange={(e) => updateChemicalRow(index, { amountOz: e.target.value })}
                    variant="outlined" size="small" type="number"
                  />
                  {form.chemicalRows.length > 1 && (
                    <IconButton size="small" onClick={() =>
                      setForm(prev => ({ ...prev, chemicalRows: prev.chemicalRows.filter((_, i) => i !== index) }))}>
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            ))}
            <Grid item xs={12}>
              <Button size="small" startIcon={<AddIcon />} onClick={() =>
                setForm(prev => ({ ...prev, chemicalRows: [...prev.chemicalRows, emptyChemicalRow()] }))}>
                Add Chemical
              </Button>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes" value={form.notes} onChange={(e) => setField('notes', e.target.value)}
                variant="outlined" size="small" fullWidth multiline rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              {handwrittenUrl ? (
                <MuiAlert severity="info">
                  Handwritten log from bulk upload will be attached to the work ticket.{' '}
                  <a href={handwrittenUrl} target="_blank" rel="noopener noreferrer">View scan</a>
                </MuiAlert>
              ) : (
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <Button size="small" variant="outlined" component="label">
                    {handwrittenFile ? 'Change Handwritten Log' : 'Attach Handwritten Log (photo/PDF)'}
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      hidden
                      onChange={(e) => setHandwrittenFile(e.target.files?.[0] || null)}
                    />
                  </Button>
                  {handwrittenFile && (
                    <Typography variant="caption" color="textSecondary">
                      {handwrittenFile.name} — will be attached to the work ticket for posterity
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>
            {duplicateWarning && (
              <Grid item xs={12}>
                <MuiAlert severity="warning">
                  A spray application is already recorded for this property on this service date — this may be a duplicate entry.
                  <Box mt={1}>
                    <Button size="small" variant="outlined" color="primary" onClick={() => handleSubmit(true)} disabled={submitting}>
                      Log Anyway
                    </Button>
                  </Box>
                </MuiAlert>
              </Grid>
            )}
            {submitError && (
              <Grid item xs={12}>
                <Typography variant="body2" color="error">{submitError}</Typography>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <>
            {anyStepFailed && (
              <Button
                startIcon={retrying ? <CircularProgress size={14} /> : <ReplayIcon />}
                onClick={handleRetry} disabled={retrying} color="primary"
              >
                Retry Failed Steps
              </Button>
            )}
            <Box flexGrow={1} />
            <Button variant="contained" color="primary" onClick={handleClose}>Done</Button>
          </>
        ) : (
          <>
            {!validation.valid && (
              <Typography variant="caption" color="textSecondary" style={{ marginRight: 'auto', paddingLeft: 8 }}>
                Needed: {validation.errors.slice(0, 4).join(', ')}{validation.errors.length > 4 ? '…' : ''}
              </Typography>
            )}
            <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
            <Button
              variant="contained" color="primary"
              onClick={() => handleSubmit(false)} disabled={!validation.valid || submitting}
            >
              {submitting ? <CircularProgress size={20} /> : 'Save Spray Log'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default ManualSprayLogDialog
